import type { TimeRange } from 'features/dashboard/components/ConsumptionStats'
import type { TimeSavedChartPoint } from 'features/dashboard/components/TimeSavedChart'
import type { MatchingCategoryAverages } from 'features/dashboard/components/SkillsRadarChart'
import type { RecentOfferStatsItem } from 'lib/api'

type CachedTimeSavedChartPoint = Omit<TimeSavedChartPoint, 'interviewMinutes'>

export interface DashboardStatsSnapshot {
  analyzedCvCount: number
  offersCount: number
  matchingCount: number
  manualMatchingTimeMinutes: number
  timeSavedMinutes: number
  timeSavedChartData: CachedTimeSavedChartPoint[]
  interviewAudioPercent: number
  interviewTimeSavedMinutes: number
  hiredCandidatesCount: number
  aiInterviewCount: number
  recentOffersStats: RecentOfferStatsItem[]
  categoryAverages: MatchingCategoryAverages | null
  categoryAveragesCount: number
}

export const DASHBOARD_MANUAL_MATCHING_TIME_KEY = 'dashboardManualMatchingTimeMinutes'

export function getDashboardStatsCacheKey(
  selectedOrgId: number | null,
  userOrgId: number | null | undefined,
  timeRange: TimeRange,
): string {
  const orgPart = selectedOrgId ?? userOrgId ?? 'default'
  return `dashboardStats:${orgPart}:${timeRange}`
}

export function readDashboardStatsCache(key: string): DashboardStatsSnapshot | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw) as DashboardStatsSnapshot
    if (!parsed || typeof parsed !== 'object') return null

    return parsed
  } catch {
    return null
  }
}

export function writeDashboardStatsCache(key: string, snapshot: DashboardStatsSnapshot) {
  try {
    sessionStorage.setItem(key, JSON.stringify(snapshot))
  } catch {
    // Ignore storage quota errors.
  }
}

export function readCachedManualMatchingTimeMinutes(): number | null {
  const fromSession = sessionStorage.getItem(DASHBOARD_MANUAL_MATCHING_TIME_KEY)
  if (!fromSession) return null

  const parsed = Number(fromSession)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function cacheManualMatchingTimeMinutes(minutes: number) {
  if (Number.isFinite(minutes) && minutes > 0) {
    sessionStorage.setItem(DASHBOARD_MANUAL_MATCHING_TIME_KEY, String(minutes))
  }
}

export function updateManualMatchingTimeInDashboardCaches(minutes: number) {
  cacheManualMatchingTimeMinutes(minutes)

  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index)
    if (!key?.startsWith('dashboardStats:')) continue

    const snapshot = readDashboardStatsCache(key)
    if (!snapshot) continue

    writeDashboardStatsCache(key, {
      ...snapshot,
      manualMatchingTimeMinutes: minutes,
    })
  }
}

export function getInitialManualMatchingTimeMinutes(snapshot: DashboardStatsSnapshot | null): number {
  if (snapshot?.manualMatchingTimeMinutes && snapshot.manualMatchingTimeMinutes > 0) {
    return snapshot.manualMatchingTimeMinutes
  }

  return readCachedManualMatchingTimeMinutes() ?? 2
}
